import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import SiteHeader from "@/components/SiteHeader";
import { PLATFORM_HELP, type HelpItem } from "@/lib/safety/helpContent";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, MessageCircle, Send, ChevronDown, HelpCircle, ShieldCheck } from "lucide-react";

/* ── Ask RAELA section ── */
function AskRaela() {
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  // Flatten all FAQ items for local search
  const allFaq: HelpItem[] = PLATFORM_HELP.flatMap((s) => s.items);

  const handleAsk = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setAnswer("");

    // First try local FAQ match (simple keyword)
    const q = query.toLowerCase();
    const match = allFaq.find(
      (item) =>
        item.q.toLowerCase().includes(q) ||
        item.a.toLowerCase().includes(q) ||
        q.split(" ").some((w) => w.length > 3 && item.q.toLowerCase().includes(w))
    );

    if (match) {
      setAnswer(match.a);
      setLoading(false);
      return;
    }

    // Fall back to RAELA edge function
    try {
      const { data, error } = await supabase.functions.invoke("raela-chat", {
        body: {
          message: query,
          context: "faq",
          appId: "wavlynq",
        },
      });
      if (error) throw error;
      setAnswer(data?.reply ?? "I couldn't find an answer right now. Please submit a concern below and our team will get back to you.");
    } catch {
      setAnswer(
        "I'm having trouble connecting right now. Please try again or submit a concern below — our team will get back to you."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
          <MessageCircle className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Ask RAELA</h2>
          <p className="text-sm text-muted-foreground">
            Type your question and RAELA will search our FAQ for you.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="e.g. How do I cancel my subscription?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAsk()}
            className="pl-10"
          />
        </div>
        <Button onClick={handleAsk} disabled={loading || !query.trim()}>
          {loading ? "Searching…" : "Ask"}
        </Button>
      </div>

      {answer && (
        <div className="rounded-xl border bg-card p-5 text-sm leading-relaxed animate-in fade-in-0 slide-in-from-bottom-2">
          <p className="font-semibold text-primary mb-1">RAELA says:</p>
          <p className="text-foreground">{answer}</p>
        </div>
      )}
    </section>
  );
}

/* ── FAQ Accordion ── */
function FaqAccordion() {
  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
          <HelpCircle className="h-5 w-5 text-accent" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
          <p className="text-sm text-muted-foreground">Browse by category.</p>
        </div>
      </div>

      {PLATFORM_HELP.map((section) => (
        <div key={section.id} className="space-y-2">
          <h3 className="font-semibold text-base">{section.title}</h3>
          {section.description && (
            <p className="text-sm text-muted-foreground">{section.description}</p>
          )}
          <Accordion type="single" collapsible className="w-full">
            {section.items.map((item, idx) => (
              <AccordionItem key={idx} value={`${section.id}-${idx}`}>
                <AccordionTrigger className="text-left text-sm">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}
    </section>
  );
}

/* ── Concern Form ── */
const CONCERN_CATEGORIES = [
  "Account issue",
  "Billing & subscriptions",
  "Safety concern",
  "Report a bug",
  "Feature request",
  "Harassment or abuse",
  "Privacy & data request",
  "Other",
] as const;

function ConcernForm() {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || submitted) return;
    setSubmitting(true);

    const fd = new FormData(e.currentTarget);
    const name = (fd.get("name") as string)?.trim();
    const email = (fd.get("email") as string)?.trim();
    const category = (fd.get("category") as string)?.trim();
    const message = (fd.get("message") as string)?.trim();

    if (!name || !email || !category || !message) {
      toast.error("Please fill in all fields.");
      setSubmitting(false);
      return;
    }

    try {
      // Send concern via the partner email function repurposed, or direct to support
      const { error } = await supabase.functions.invoke(
        "send-partner-application-email",
        {
          body: {
            fullName: name,
            email,
            partnerType: "other",
            pitch: `[${category}] ${message}`,
            applicationId: `concern-${Date.now()}-${email}`,
          },
        }
      );
      if (error) throw error;

      setSubmitted(true);
      toast.success("Your concern has been submitted. We'll get back to you soon.");
    } catch (err) {
      console.error("Concern submit error:", err);
      toast.error("Something went wrong. Please try again or email support@wavlynq.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center space-y-3">
        <ShieldCheck className="h-10 w-10 text-success mx-auto" />
        <p className="font-semibold text-lg">Concern received</p>
        <p className="text-sm text-muted-foreground">
          Our team typically responds within 1–2 business days. You'll hear from us at the email you provided.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
          <Send className="h-5 w-5 text-destructive" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Submit a Concern</h2>
          <p className="text-sm text-muted-foreground">
            Didn't find your answer? Let us know and we'll follow up.
          </p>
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="concern-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="concern-name"
              name="name"
              required
              defaultValue={user?.user_metadata?.full_name ?? ""}
              placeholder="Your name"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="concern-email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="concern-email"
              name="email"
              type="email"
              required
              defaultValue={user?.email ?? ""}
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="concern-category" className="text-sm font-medium">
            Category
          </label>
          <Select name="category" required>
            <SelectTrigger id="concern-category">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {CONCERN_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="concern-message" className="text-sm font-medium">
            Describe your concern
          </label>
          <Textarea
            id="concern-message"
            name="message"
            required
            rows={5}
            placeholder="Tell us what happened or what you need help with…"
          />
        </div>

        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? "Submitting…" : "Submit concern"}
        </Button>
      </form>
    </section>
  );
}

/* ── Main Support page ── */
export default function Support() {
  return (
    <>
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 py-12 space-y-16">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Support</h1>
          <p className="text-muted-foreground">
            Ask RAELA, browse our FAQ, or submit a concern — we're here to help.
          </p>
        </div>

        <AskRaela />

        <hr className="border-border" />

        <FaqAccordion />

        <hr className="border-border" />

        <ConcernForm />
      </main>
      <SiteFooter />
    </>
  );
}
